





import { formatWaveLabel, lineTypeForDegree, styleForDegree } from './ElliottDegreeStyles';
import { findDateIndex } from './overlayUtils';
import { octavesFor, bandSeries } from './planetaryLinePrice';

const formatClassicAspectCell = (item, isDecl = false) => {
  if (!item || typeof item !== 'object') return null;

  if (isDecl) {
    const declValue = Number.isFinite(item.value) ? item.value.toFixed(2) : 'n/a';
    return `${item.pair || 'Planet'} decl ${declValue}°`;
  }

  const pair = item.pair || 'Pair';
  const aspect = item.aspect || 'Aspect';
  const orb = Number.isFinite(item.orbDeg) ? item.orbDeg.toFixed(2) : 'n/a';
  return `${pair} ${aspect} (orb ${orb}°)`;
};

const renderTwoColumnAspectRows = (items, isDecl = false) => {
  if (!Array.isArray(items) || items.length === 0) return '';
  const cells = items
    .map((item) => formatClassicAspectCell(item, isDecl))
    .filter(Boolean);
  if (cells.length === 0) return '';

  const rows = [];
  for (let i = 0; i < cells.length; i += 2) {
    const left = cells[i] || '';
    const right = cells[i + 1] || '';
    rows.push(
      `<div style="display:flex;gap:10px;margin:1px 0;">` +
      `<div style="flex:1;min-width:180px;">${left}</div>` +
      `<div style="flex:1;min-width:180px;">${right}</div>` +
      `</div>`
    );
  }
  return `<div style="margin-top:2px;">${rows.join('')}</div>`;
};







const getMarkings = (overlays, barData) => {
  // Defensive: Filter out undefined/null overlays
  const filteredOverlays = Array.isArray(overlays) ? overlays.filter(o => o && typeof o === 'object' && o.type) : [];
  if (Array.isArray(overlays) && overlays.some(o => !o || typeof o !== 'object' || !o.type)) {
    console.warn('[getMarkings] WARNING: overlays array contained undefined, null, or malformed overlay(s):', overlays);
  }
  overlays = filteredOverlays;
  // --- BEGIN DIAGNOSTIC LOGGING ---
  console.log('[getMarkings] Initial overlays received:', overlays);
  if (!barData || !barData.dates) {
    console.warn('[getMarkings] barData missing or invalid:', barData);
  } else {
    console.log('[getMarkings] barData.dates:', barData.dates);
  }
  // --- END DIAGNOSTIC LOGGING ---
  const { dates } = barData;
  const markLines = [];
  const markAreas = [];
  const trendLines = [];
  const planetaryLineSeries = [];

  if (!Array.isArray(overlays) || overlays.length === 0) {
    // No overlays to render
    return { markLines, markAreas, trendLines, planetaryLineSeries };
  }

  // If overlays are present but no planetaryLine overlay is found, log a diagnostic
  const hasPlanetaryLine = overlays.some(ovr => (ovr.type || '').toLowerCase() === 'planetaryline');
  if (!hasPlanetaryLine) {
    console.warn('[getMarkings] No planetaryLine overlay present in overlays array.');
  }

  overlays.forEach((overlay, index) => {
    if (!overlay || !overlay.type) {
      console.warn(`[getMarkings] Skipping invalid overlay #${index}:`, overlay);
      return;
    }

    // Log overlay fields for debugging
    Object.keys(overlay).forEach(key => {
      if (overlay[key] === undefined) {
        console.warn(`[getMarkings] Overlay #${index} missing field:`, key);
      }
    });

    const type = overlay.type.toLowerCase();
    console.log(`[getMarkings] Processing overlay #${index} of type '${type}'`, overlay);

    if (type.startsWith('vline') || type.startsWith('verticalline')) {
      const dateIndex = findDateIndex(overlay.date, dates);
      console.log(`[getMarkings] vLine: date='${overlay.date}', found index=${dateIndex}`);
      if (dateIndex !== -1) {
        const markLine = {
          name: overlay.label || '',
          xAxis: dates[dateIndex],
          // lineType is honoured so a projected, not-yet-confirmed level can draw dashed while a
          // confirmed one stays solid. Defaults to solid, so every existing vLine is unaffected.
          lineStyle: { color: overlay.color || '#000000', width: overlay.thickness || 1, type: overlay.lineType || 'solid' },
          label: {
            show: true,
            position: 'insideStartTop',
            rotate: 90,
            distance: 15,
            formatter: (params) => {
              const [line1, line2] = params.name.split('\n');
              // If customLabelColor, prepend icon; else just label
              if (overlay.customLabelColor) {
                if (line2) {
                  return `{icon|}{line1|${line1}}\n{line2|${line2}}`;
                }
                return `{icon|}{line1|${line1}}`;
              } else {
                if (line2) {
                  return `{line1|${line1}}\n{line2|${line2}}`;
                }
                return `{line1|${line1}}`;
              }
            },
            rich: {
              icon: {
                width: 12,
                height: 12,
                borderRadius: 6, // circle; set to 2 for square
                backgroundColor: overlay.customLabelColor || 'transparent',
                borderColor: overlay.customLabelColor || 'transparent',
                borderWidth: 1,
                align: 'left',
                verticalAlign: 'middle',
                padding: [0, 4, 0, 0], // right space after icon
              },
              line1: {
                color: '#333',
                fontSize: 12,
                fontWeight: 'bold',
                align: 'left',
              },
              line2: {
                color: '#666',
                fontSize: 10,
                align: 'left',
                padding: [4, 0, 0, 0],
              },
            },
          },
        };
        console.log('[getMarkings] Creating markLine:', markLine);
        markLines.push(markLine);
      } else {
        console.warn(`[getMarkings] Skipping vLine overlay due to invalid date index. Label: '${overlay.label}', Date: '${overlay.date}', Index: ${dateIndex}`);
        // Explicitly log the overlay that is not displayed
        window.skippedOverlays = window.skippedOverlays || [];
        window.skippedOverlays.push({ type: 'vLine', label: overlay.label, date: overlay.date, index: dateIndex });
      }
    } else if (type === 'areahighlight') {
      const startIndex = findDateIndex(overlay.date1, dates);
      const endIndex = findDateIndex(overlay.date2, dates);
      console.log('[OverlayDebug] getMarkings areaHighlight:', overlay.date1, overlay.date2, 'startIndex:', startIndex, 'endIndex:', endIndex, 'dates.length:', dates.length);
      console.log(`[getMarkings] areaHighlight: date1='${overlay.date1}', found startIndex=${startIndex}`);
      console.log(`[getMarkings] areaHighlight: date2='${overlay.date2}', found endIndex=${endIndex}`);
      if (startIndex !== -1 && endIndex !== -1) {
        const markArea = [
          { name: overlay.label || '', xAxis: dates[startIndex], itemStyle: { color: overlay.color } },
          { xAxis: dates[endIndex] },
        ];
        console.log('[getMarkings] Creating markArea:', markArea);
        markAreas.push(markArea);
      } else {
        console.warn(`[getMarkings] Skipping areaHighlight due to invalid date index. Label: '${overlay.label}', Start: '${overlay.date1}' -> ${startIndex}, End: '${overlay.date2}' -> ${endIndex}`);
        // Explicitly log the overlay/date pair that is not displayed
        window.skippedOverlays = window.skippedOverlays || [];
        window.skippedOverlays.push({ type: 'areaHighlight', label: overlay.label, date1: overlay.date1, date2: overlay.date2, startIndex, endIndex });
      }
    } else if (type === 'elliottwave') {
      /*
       * An Elliott count drawn as a polyline through its pivots, with each pivot labelled.
       *
       * Built as an array ALIGNED to the dates axis with '-' between pivots and connectNulls, not
       * as a list of [x, y] pairs. The price panel's x-axis is a category axis of date strings: a
       * pair-based series has to match a category exactly to land, and anything that does not match
       * is dropped silently — a polyline with invisible holes and no error anywhere. Aligning to
       * the axis by index removes the question.
       *
       * That is also why the backend sends barIndex alongside timestamp. The index addresses the
       * bar list the frontend itself supplied, so it resolves here without re-deriving anything.
       */
      const points = Array.isArray(overlay.points) ? overlay.points : [];
      if (points.length < 2) {
        console.warn('[getMarkings] Skipping elliottWave overlay with too few pivots:', overlay);
        return;
      }
      const style = styleForDegree(overlay.degree);
      const aligned = new Array(dates.length).fill('-');
      let placed = 0;

      points.forEach(point => {
        const index = Number(point.barIndex);
        if (!Number.isInteger(index) || index < 0 || index >= dates.length) return;
        if (typeof point.price !== 'number' || !Number.isFinite(point.price)) return;

        const text = formatWaveLabel(point.label, overlay.degree);
        aligned[index] = text
          ? {
              value: point.price,
              label: {
                show: true,
                formatter: text,
                // Above a peak, below a trough — using the pivot's own kind rather than guessing
                // from neighbouring prices, which could disagree with the swing detector.
                position: point.high ? 'top' : 'bottom',
                color: '#fff',
                backgroundColor: style.color,
                borderRadius: 3,
                padding: [2, 5],
                fontSize: 11,
                fontWeight: 'bold',
              },
            }
          : point.price;
        placed++;
      });

      if (placed < 2) {
        console.warn('[getMarkings] elliottWave overlay resolved to fewer than 2 on-chart pivots');
        return;
      }

      planetaryLineSeries.push({
        id: `__elliott__${overlay.id || index}`,
        type: 'line',
        name: overlay.label || `Elliott ${style.name}`,
        data: aligned,
        // The pivots are sparse by construction; without this the line would not join them.
        connectNulls: true,
        showSymbol: true,
        symbol: 'circle',
        symbolSize: 7,
        itemStyle: { color: style.color },
        lineStyle: {
          color: style.color,
          width: style.width,
          type: lineTypeForDegree(overlay.degree),
        },
        emphasis: { disabled: true },
        z: 120,
        xAxisIndex: 0,
        yAxisIndex: 0,
        tooltip: { show: false },
      });
    } else if (type === 'astrographline') {
      if (Array.isArray(overlay.data) && overlay.data.length > 0) {
        const pointMap = {};
        overlay.data.forEach(pt => {
          pointMap[(pt.date || '').substring(0, 10)] = {
            value: pt.value,
            longTerm: pt.longTerm,
            middleTerm: pt.middleTerm,
            decl: pt.decl,
            rawValue: pt.rawValue,
            longAspects: Array.isArray(pt.longAspects) ? pt.longAspects : null,
            middleAspects: Array.isArray(pt.middleAspects) ? pt.middleAspects : null,
            declAspects: Array.isArray(pt.declAspects) ? pt.declAspects : null,
          };
        });
        // Use simple numbers for chart rendering — extra props on objects can
        // confuse ECharts into treating them as data dimensions (causes spikes).
        // Tooltip data is stored in a closure-captured lookup by index.
        const tooltipLookup = {};
        const seriesData = barData.dates.map((date, idx) => {
          const point = pointMap[(date || '').substring(0, 10)];
          if (!point) return '-';             // ECharts 'no data' marker (null can be treated as 0)
          const v = point.value;
          if (typeof v !== 'number' || !isFinite(v)) return '-';
          tooltipLookup[idx] = point;        // for tooltip formatter
          return v;
        });
        // Diagnostic: comprehensive data quality analysis
        let matchCount = 0, nullCount = 0, minVal = Infinity, maxVal = -Infinity;
        const numericVals = [];
        const nonNumericEntries = [];
        for (let i = 0; i < seriesData.length; i++) {
          const d = seriesData[i];
          if (typeof d === 'number' && isFinite(d)) {
            matchCount++;
            if (d < minVal) minVal = d;
            if (d > maxVal) maxVal = d;
            numericVals.push({ idx: i, val: d, date: (barData.dates[i] || '').substring(0, 10) });
          } else {
            nullCount++;
            if (d !== '-' && d !== null && d !== undefined) {
              nonNumericEntries.push({ idx: i, val: d, type: typeof d, date: (barData.dates[i] || '').substring(0, 10) });
            }
          }
        }
        // Find outliers: sort by absolute value, show top 10 extremes
        const sorted = [...numericVals].sort((a, b) => Math.abs(b.val) - Math.abs(a.val));
        const top10 = sorted.slice(0, 10);
        // Compute mean and std dev
        const mean = numericVals.reduce((s, e) => s + e.val, 0) / (numericVals.length || 1);
        const variance = numericVals.reduce((s, e) => s + (e.val - mean) ** 2, 0) / (numericVals.length || 1);
        const stdDev = Math.sqrt(variance);
        // Values beyond 3 std deviations
        const outliers = numericVals.filter(e => Math.abs(e.val - mean) > 3 * stdDev);
        console.log(`[AstroGraph DATA] total=${seriesData.length}, matched=${matchCount}, nulls=${nullCount}, range=[${minVal},${maxVal}], mean=${mean.toFixed(2)}, stdDev=${stdDev.toFixed(2)}`);
        console.log(`[AstroGraph DATA] top10 extreme values:`, top10);
        console.log(`[AstroGraph DATA] outliers (>3σ from mean): ${outliers.length}`, outliers.slice(0, 20));
        if (nonNumericEntries.length > 0) console.warn(`[AstroGraph DATA] non-numeric entries:`, nonNumericEntries.slice(0, 10));
        console.log(`[AstroGraph DATA] sample first 5:`, seriesData.slice(0, 5), `last 5:`, seriesData.slice(-5));
        const label = overlay.label || 'TrendPulse';
        const lineColor = overlay.color || '#ff6600';
        const lineSeries = {
          // Unique per overlay. Several TrendPulse curves can share a chart now, and a fixed id
          // would make ECharts treat them as one series — the last one drawn would replace the
          // rest, reproducing by another route exactly the overwrite this feature removed.
          id: `__astrograph__${overlay.id ? `_${overlay.id}` : `_${index}`}`,
          type: 'line',
          name: label,
          data: seriesData,
          symbol: 'circle',
          symbolSize: 6,
          showSymbol: true,
          triggerLineEvent: true,
          lineStyle: {
            color: lineColor,
            width: overlay.thickness || 1.5,
            type: overlay.lineType || 'solid',
            opacity: 0.7,
          },
          itemStyle: { color: '#000000' },
          z: 110,
          clip: true,
          xAxisIndex: 0,
          yAxisIndex: 6,
          connectNulls: false,
          endLabel: { show: true, formatter: label, color: 'inherit', offset: [0, -2], fontSize: 12, fontWeight: 'bold' },
          tooltip: {
            show: true,
            trigger: 'item',
            formatter: (params) => {
              const data = tooltipLookup[params.dataIndex] || null;
              const longTerm = data && Number.isFinite(data.longTerm) ? data.longTerm : null;
              const middleTerm = data && Number.isFinite(data.middleTerm) ? data.middleTerm : null;
              const decl = data && Number.isFinite(data.decl) ? data.decl : null;
              const longAspects = data && Array.isArray(data.longAspects) ? data.longAspects : [];
              const middleAspects = data && Array.isArray(data.middleAspects) ? data.middleAspects : [];
              const declAspects = data && Array.isArray(data.declAspects) ? data.declAspects : [];

              const lines = [
                `<b>${label}</b>`,
                `${params.axisValueLabel || params.name}`,
              ];

              if (longTerm !== null && middleTerm !== null && decl !== null) {
                if (longAspects.length > 0) {
                  lines.push(`<span style="font-weight:600;">Long-term aspects (${longAspects.length})</span>`);
                  lines.push(renderTwoColumnAspectRows(longAspects));
                }

                if (middleAspects.length > 0) {
                  lines.push(`<span style="font-weight:600;">Middle-term aspects (${middleAspects.length})</span>`);
                  lines.push(renderTwoColumnAspectRows(middleAspects));
                }

                if (declAspects.length > 0) {
                  lines.push(`<span style="font-weight:600;">Declination inputs</span>`);
                  lines.push(renderTwoColumnAspectRows(declAspects, true));
                }
              }

              return lines.join('<br/>');
            }
          }
        };
        console.log('[getMarkings] Creating astroGraphLine series', lineSeries);
        planetaryLineSeries.push(lineSeries);
      } else {
        console.warn('[getMarkings] Skipping astroGraphLine with empty or missing data:', overlay);
      }
    } else if (type === 'trendline') {
      if (overlay.price1 !== undefined && overlay.price2 !== undefined) {
        console.log('[getMarkings] Creating trendLine:', overlay);
        trendLines.push(overlay);
      } else {
        console.warn(`[getMarkings] Skipping trendline with missing price data. Label: '${overlay.label}', price1: ${overlay.price1}, price2: ${overlay.price2}`, overlay);
        // Explicitly log the overlay that is not displayed
        window.skippedOverlays = window.skippedOverlays || [];
        window.skippedOverlays.push({ type: 'trendLine', label: overlay.label, price1: overlay.price1, price2: overlay.price2 });
      }
    } else if (type === 'planetaryline') {
      // Validate data length matches barData.dates
      if (Array.isArray(overlay.data) && overlay.data.length === barData.dates.length) {
        // Bind each longitude value to its corresponding date for ECharts time/category x-axis
        // Normalize overlay.data to a flat array of longitude numbers
        let planetaryData = overlay.data;
        if (Array.isArray(planetaryData) && planetaryData.length > 0 && typeof planetaryData[0] === 'object' && 'value' in planetaryData[0]) {
           // Convert [{value: [date, longitude]}, ...] to [longitude, ...]
           planetaryData = planetaryData.map(pt => Array.isArray(pt.value) ? pt.value[1] : pt.value);
           
           const pricePer360 = parseFloat(overlay.pricePer360) || 200;

           if (overlay.panel === 'main') {
             if (overlay.mode === 'declination') {
               const hasDeclinationBands = planetaryLineSeries.some(series => series && series.id === 'declination-fixed-bands');
               if (!hasDeclinationBands) {
                 const bandStyle = {
                   color: 'rgba(64, 140, 255, 0.14)'
                 };
                 const boundaryLineStyle = {
                   color: 'rgba(64, 140, 255, 0.70)',
                   width: 1,
                   type: 'dashed'
                 };
                 planetaryLineSeries.push({
                   id: 'declination-fixed-bands',
                   type: 'line',
                   name: 'Declination Bands',
                   data: [],
                   symbol: 'none',
                   silent: true,
                   xAxisIndex: 0,
                   yAxisIndex: 5,
                   z: 10,
                   lineStyle: {
                     opacity: 0
                   },
                   markArea: {
                     silent: true,
                     itemStyle: bandStyle,
                     data: [
                       [
                         { yAxis: -1 },
                         { yAxis: 1 }
                       ],
                       [
                         { yAxis: 22.5 },
                         { yAxis: 23.5 }
                       ],
                       [
                         { yAxis: -23.5 },
                         { yAxis: -22.5 }
                       ]
                     ]
                   },
                   markLine: {
                     silent: true,
                     symbol: 'none',
                     label: { show: false },
                     data: [
                       { yAxis: -1, lineStyle: boundaryLineStyle },
                       { yAxis: 0, lineStyle: { color: 'rgba(32, 100, 220, 0.95)', width: 1.5, type: 'solid' } },
                       { yAxis: 1, lineStyle: boundaryLineStyle },
                       { yAxis: 22.5, lineStyle: boundaryLineStyle },
                       { yAxis: 23.5, lineStyle: boundaryLineStyle },
                       { yAxis: -23.5, lineStyle: boundaryLineStyle },
                       { yAxis: -22.5, lineStyle: boundaryLineStyle }
                     ]
                   }
                 });
               }
               // --- Single line for Declination (Main Panel) ---
               const declinationData = barData.dates.map((date, i) => [date, planetaryData[i]]);
               const declinationLine = {
                   type: 'line',
                   name: `${overlay.planet || 'Declination'}`,
                   data: declinationData,
                   symbol: 'none',
                   lineStyle: {
                       color: overlay.color || '#00AAFF',
                       width: overlay.thickness === 'thick' ? 3 : overlay.thickness === 'medium' ? 2 : 1,
                       type: overlay.lineType || 'solid',
                   },
                   z: 100,
                   xAxisIndex: 0,
                   yAxisIndex: 5, // Use the dedicated Declination axis (Auto-scaled)
                   endLabel: {
                     show: true,
                     formatter: '{a}',
                     color: 'inherit',
                     offset: [0, -2],
                     fontSize: 14,
                     fontWeight: 'bold'
                   },
                   markPoint: {
                     symbol: 'pin',
                     symbolSize: 50,
                     label: {
                        show: true,
                        formatter: overlay.planet || 'Declination',
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 'bold'
                     },
                     itemStyle: { color: overlay.color || '#00AAFF' },
                     data: [
                       {
                         coord: declinationData[declinationData.length - 1],
                         value: overlay.planet || 'Declination'
                       }
                     ]
                   },
                   emphasis: {
                     focus: 'series',
                     label: {
                       show: true,
                       formatter: '{a}',
                       position: 'top',
                       fontSize: 12,
                       fontWeight: 'bold',
                       color: 'inherit'
                     }
                   },
                   tooltip: {
                     show: true,
                     trigger: 'item',
                     formatter: (params) => {
                       return `${params.seriesName}: ${params.value[1].toFixed(2)}°`;
                     }
                   }
               };
               console.log('[getMarkings] Creating Declination line', declinationLine);
               planetaryLineSeries.push(declinationLine);

             } else {
               // --- Repeated planetary lines for main panel (Longitude) ---
               //
               // planetaryData holds PRICES, not degrees: App.js already unwrapped the longitude
               // series and scaled it by pricePer360/360. The mapping here used to be
               // `(value % 360) * pricePer360 / 360`, which scaled a second time and took a
               // modulus at 360 price units — so for Venus at pricePer360=200 the band repeated
               // every 405 days against a 225-day orbit, and reset at 288 degrees rather than at
               // Aries. Reducing modulo pricePer360 makes one band exactly one revolution again.
               const minPrice = Math.min(...(barData.ohlc?.map(bar => bar[2]) || [0])); // low
               const maxPrice = Math.max(...(barData.ohlc?.map(bar => bar[1]) || [0])); // high
               const octaves = octavesFor(overlay, minPrice, maxPrice, pricePer360);
               const numReps = octaves.length;
               for (let rep = 0; rep < numReps; ++rep) {
                   const k = octaves[rep];
                   // bandSeries inserts a null at each revolution boundary so ECharts starts a new
                   // path instead of drawing a full-height vertical line back down the chart.
                   const seriesData = bandSeries(barData.dates, planetaryData, pricePer360, minPrice, k);
                   const planetaryLine = {
                   type: 'line',
                   name: `${overlay.planet || 'Planetary Line'}${numReps > 1 ? ' (N=' + k + ')' : ''}`,
                   data: seriesData,
                   symbol: 'none',
                   lineStyle: {
                       color: overlay.color || '#00AAFF',
                       width: overlay.thickness === 'thick' ? 3 : overlay.thickness === 'medium' ? 2 : 1,
                       type: overlay.lineType || 'solid',
                   },
                   z: 100,
                   xAxisIndex: 0,
                   yAxisIndex: 0,
                   endLabel: {
                     show: true,
                     formatter: '{a}',
                     color: 'inherit',
                     offset: [0, -2],
                     fontSize: 14,
                     fontWeight: 'bold'
                   },
                   markPoint: {
                     symbol: 'pin',
                     symbolSize: 50,
                     label: {
                        show: true,
                        formatter: overlay.planet || 'Planet',
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 'bold'
                     },
                     itemStyle: { color: overlay.color || '#00AAFF' },
                     data: [
                       {
                         coord: [...seriesData].reverse().find(pt => pt && pt[1] !== null) || seriesData[seriesData.length - 1],
                         value: overlay.planet || 'Planet'
                       }
                     ]
                   },
                   emphasis: {
                     focus: 'series',
                     label: {
                       show: true,
                       formatter: '{a}',
                       position: 'top',
                       fontSize: 12,
                       fontWeight: 'bold',
                       color: 'inherit'
                     }
                   },
                   tooltip: {
                     show: true,
                     trigger: 'item',
                     formatter: (params) => {
                       return `${overlay.planet || 'Planet'}: ${params.value[1].toFixed(2)}`;
                     }
                   }
                   };
                   console.log('[getMarkings] Creating planetaryLine repetition', k+1, 'of', numReps, planetaryLine);
                   planetaryLineSeries.push(planetaryLine);
               }
             }
           } else if (overlay.panel === 'volume') {
             // Map longitude 0->360 for planetary lines in volume panel using Dedicated Axis (Index 4)
             const volumeLineData = barData.dates.map((date, i) => [date, planetaryData[i]]);
             const planetaryLine = {
               type: 'line',
               name: overlay.planet || 'Planetary Line',
               data: volumeLineData,
               symbol: 'none',
               lineStyle: {
                 color: overlay.color || '#00AAFF',
                 width: overlay.thickness === 'thick' ? 3 : overlay.thickness === 'medium' ? 2 : 1,
                 type: overlay.lineType || 'solid',
               },
               z: 100,
               xAxisIndex: 1,
               yAxisIndex: 4, // Use dedicated Longitude axis
               endLabel: {
                 show: true,
                 formatter: '{a}',
                 color: 'inherit',
                 offset: [0, -2],
                 fontSize: 14,
                 fontWeight: 'bold'
               },
               markPoint: {
                 symbol: 'pin',
                 symbolSize: 50,
                 label: {
                    show: true,
                    formatter: overlay.planet || 'Planet',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 'bold'
                 },
                 itemStyle: { color: overlay.color || '#00AAFF' },
                 data: [
                   {
                     coord: volumeLineData[volumeLineData.length - 1],
                     value: overlay.planet || 'Planet'
                   }
                 ]
               },
               emphasis: {
                 focus: 'series',
                 label: {
                   show: true,
                   formatter: '{a}',
                   position: 'top',
                   fontSize: 12,
                   fontWeight: 'bold',
                   color: 'inherit'
                 }
               },
               tooltip: {
                 show: true,
                 trigger: 'item',
                 formatter: (params) => {
                   return `${overlay.planet || 'Planet'}: ${params.value[1].toFixed(2)}°`;
                 }
               }
             };
             planetaryLineSeries.push(planetaryLine);
           }
        }
      } else {
        console.warn(`[getMarkings] Skipping planetaryLine with missing or mismatched data:`, overlay);
      }
    } else if (type === 'characteristicline') {
      if (Array.isArray(overlay.data) && overlay.data.length === barData.dates.length) {
        const metric = overlay.characteristicType || 'characteristic';
        const isMain = overlay.panel === 'main';
        let yAxisIndex = 7;
        let unit = '';
        let formatter = (v) => {
          if (v === null || v === undefined || isNaN(v)) return '';
          return Number(v).toFixed(2);
        };

        if (metric === 'speed') {
          yAxisIndex = isMain ? 10 : 7;
          unit = '°/day';
          formatter = (v) => `${Number(v).toFixed(3)}`;
        } else if (metric === 'distance_percent' || metric === 'distance_percent_diff') {
          yAxisIndex = isMain ? 11 : 8;
          unit = '%';
          formatter = (v) => `${Number(v).toFixed(1)}`;
        } else if (metric === 'longitude_diff' || metric === 'declination_diff') {
          yAxisIndex = isMain ? 12 : 9;
          unit = '°';
          formatter = (v) => `${Number(v).toFixed(2)}`;
        }

        const lineData = overlay.data.map(pt => Array.isArray(pt.value) ? pt.value : pt);
        const series = {
          type: 'line',
          name: overlay.label || overlay.characteristicType || 'Characteristic',
          data: lineData,
          symbol: 'none',
          showSymbol: false,
          connectNulls: false,
          lineStyle: {
            color: overlay.color || '#E67C73',
            width: Number(overlay.thickness || 2),
            type: overlay.lineType || 'solid',
          },
          z: 90,
          xAxisIndex: isMain ? 0 : 1,
          yAxisIndex,
          endLabel: { show: true, formatter: '{a}', color: 'inherit', offset: [0, -2], fontSize: 12, fontWeight: 'bold' },
          tooltip: {
            show: true,
            trigger: 'item',
            formatter: (params) => {
              const val = Array.isArray(params.value) ? params.value[1] : params.value;
              return `${params.seriesName}: ${formatter(val)}${unit}`;
            }
          }
        };
        planetaryLineSeries.push(series);
      } else {
        console.warn(`[getMarkings] Skipping characteristicLine with missing or mismatched data:`, overlay);
      }
    } else if (type === 'horizontalline') {
      // Horizontal price line drawn across the full chart
      if (overlay.price !== undefined) {
        markLines.push({
          name: overlay.description || `${overlay.price}`,
          yAxis: overlay.price,
          lineStyle: { color: overlay.color || '#e53935', width: 1.5, type: 'dashed' },
          label: {
            show: true,
            position: 'insideEndTop',
            formatter: overlay.description || `${overlay.price}`,
            color: overlay.color || '#e53935',
            fontSize: 11,
          },
        });
      }
    } else if (type === 'seasonalityprojection') {
      if (Array.isArray(overlay.projections) && overlay.projections.length > 0) {
        const displayType = overlay.displayType || 'candlestick';

        // Build index-based sparse arrays for category axis alignment
        const projOhlcData = new Array(dates.length).fill('-');
        const projCloseLineData = new Array(dates.length).fill(null);

        overlay.projections.forEach(p => {
          const idx = findDateIndex(p.date, dates);
          if (idx !== -1) {
            projOhlcData[idx] = [p.projectedOpen, p.projectedClose, p.projectedLow, p.projectedHigh];
            projCloseLineData[idx] = p.projectedClose;
          }
        });

        if (displayType === 'candlestick') {
          // Projected candlestick series (semi-transparent)
          planetaryLineSeries.push({
            type: 'candlestick',
            name: 'Seasonality Projection',
            data: projOhlcData,
            barWidth: 8,
            itemStyle: {
              color: 'rgba(76, 175, 80, 0.6)',
              color0: 'rgba(244, 67, 54, 0.6)',
              borderColor: 'rgba(76, 175, 80, 0.9)',
              borderColor0: 'rgba(244, 67, 54, 0.9)',
            },
            z: 80,
          });
        }

        // Projected close line (always shown for line mode, also shown for candlestick)
        planetaryLineSeries.push({
          type: 'line',
          name: 'Projected Close',
          data: projCloseLineData,
          symbol: 'circle',
          symbolSize: 4,
          connectNulls: false,
          lineStyle: { color: '#1976d2', width: 2, type: displayType === 'line' ? 'solid' : 'dashed' },
          itemStyle: { color: '#1976d2' },
          z: 81,
        });
      }
    } else if (type === 'pivotmarkers') {
      // One overlay carries every dot of a study run, rendered as a single scatter series rather
      // than one series per dot: a study can mark dozens of pivots, and a series each would make
      // the option object enormous and the legend meaningless.
      //
      // Not attached to the candlestick series' markPoint, which the candlestick-pattern feature
      // already owns — a second writer there would silently replace the first one's dots.
      const points = Array.isArray(overlay.points) ? overlay.points : [];
      const data = points
        .filter(p => p && p.date !== undefined && typeof p.price === 'number')
        .map(p => {
          const above = p.position !== 'below';
          return {
            value: [p.date, p.price],
            // Offset off the bar so a dot sits clear of the wick instead of on top of it.
            symbolOffset: [0, above ? -12 : 12],
            // Per-point rotation, so one overlay can carry both up and down arrows.
            ...(typeof p.symbolRotate === 'number' ? { symbolRotate: p.symbolRotate } : {}),
            itemStyle: { color: p.color || overlay.color || '#6a1b9a' },
            label: p.label
              ? {
                  show: true,
                  position: above ? 'top' : 'bottom',
                  formatter: p.label,
                  // Bold and a couple of points up: these numbers are the study's output, read
                  // against candles and axis labels, and at 9px regular they were hard to pick out.
                  fontSize: overlay.labelFontSize || 12,
                  fontWeight: 'bold',
                  color: p.color || overlay.color || '#6a1b9a',
                  backgroundColor: 'rgba(255,255,255,0.85)',
                  borderRadius: 2,
                  padding: [1, 4],
                }
              : { show: false },
          };
        });
      if (data.length > 0) {
        planetaryLineSeries.push({
          id: `pivot-markers-${overlay.id || Math.random().toString(36).slice(2)}`,
          name: overlay.label || 'Pivot markers',
          type: 'scatter',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data,
          // 'emptyCircle' is ECharts' outlined variant, which is what makes one tool draw a ring
          // and the other a disc from the same renderer.
          symbol: overlay.symbol || 'circle',
          symbolSize: overlay.symbolSize || 7,
          z: 90,
          tooltip: { show: true },
        });
      }
    } else if (type === 'drawrect') {
      // Rectangle drawn between two diagonal corners (date1,price1) and (date2,price2)
      const startIndex = findDateIndex(overlay.date1, dates);
      const endIndex = findDateIndex(overlay.date2, dates);
      if (startIndex !== -1 && endIndex !== -1) {
        const d1 = dates[Math.min(startIndex, endIndex)];
        const d2 = dates[Math.max(startIndex, endIndex)];
        const p1 = Math.min(overlay.price1, overlay.price2);
        const p2 = Math.max(overlay.price1, overlay.price2);
        markAreas.push([
          // borderType lets a congested band draw a dotted edge — see utils/bandDensity. ECharts
          // defaults it to 'solid', so overlays that do not set it are unaffected.
          { xAxis: d1, yAxis: p2, itemStyle: { color: overlay.color || 'rgba(255,152,0,0.18)', borderColor: overlay.borderColor || '#ff9800', borderWidth: 1.5, borderType: overlay.borderType || 'solid' } },
          { xAxis: d2, yAxis: p1 },
        ]);
      }
    }
  });

  // --- BEGIN FINAL DIAGNOSTIC LOGGING ---
  console.log('[getMarkings] Final markLines:', markLines);
  console.log('[getMarkings] Final markAreas:', markAreas);
  console.log('[getMarkings] Final trendLines:', trendLines);
  console.log('[getMarkings] Final planetaryLineSeries:', planetaryLineSeries);
  // --- END FINAL DIAGNOSTIC LOGGING ---
  return { markLines, markAreas, trendLines, planetaryLineSeries };
};

import { detectAllPatterns } from './candlestickPatterns';
import { formatTrendlineBadge, MANUAL_DRAW_SOURCE, badgeSidesByOverlayId } from './trendlineMeasure';

function getPatternColor(patterns) {
  if (!patterns || patterns.length === 0) return '#e53935';
  const pattern = patterns[0];
  switch (pattern) {
    case 'Hammer': return '#ff9800';
    case 'Doji': return '#9c27b0';
    case 'Bearish Engulfing Pattern': return '#e53935';
    case 'Bullish Marubozu': return '#2196f3';
    case 'Bearish Marubozu': return '#f44336';
    case 'Three Black Crows': return '#607d8b';
    case 'Three White Soldiers': return '#4caf50';
    case 'Morning Star': return '#00bcd4';
    case 'Evening Star': return '#ff5722';
    case 'Morning Doji Star': return '#00bcd4';
    case 'Evening Doji Star': return '#ff5722';
    default: return '#e53935';
  }
}

// patternVisibility is an object mapping pattern names to booleans
// maConfigs: array of { period, color, enabled }
export const processChartData = (barData, chartColors, overlays, viewMode, chartType, showPatterns = false, patternVisibility, maConfigs = []) => {
  const { markLines, markAreas, trendLines, planetaryLineSeries } = getMarkings(overlays, barData);

  // Which side each hand-drawn badge takes. Computed once from the full overlay list rather than
  // from trendLines, so the alternation follows chart order — the same numbering the journal uses,
  // which keeps "Line 1" in the summary and the first-sided badge the same line.
  const badgeSides = badgeSidesByOverlayId(overlays);

  const trendLineSeries = trendLines.map((line, index) => {
    // Always use the color set by createTrendLineOverlay; never override here
    const lineColor = line.color;

    // Measurement badge, for hand-drawn trendlines only. Study output uses this same overlay type
    // for its connector lines, and labelling those would put a "gain" on something nobody traded.
    const badge = line.source === MANUAL_DRAW_SOURCE
      ? formatTrendlineBadge(line.price1, line.price2)
      : '';
    // Alternating sides keep badges apart where several lines end near the same bar. Falls back to
    // the series index if an overlay somehow has no id, so the alternation never collapses to one
    // side just because a line was created without one.
    const badgeSide = badgeSides[line.id] || (index % 2 === 0 ? 'left' : 'right');

    return {
      type: 'line',
      name: `Trendline ${index + 1}`,
      data: [
        { name: 'start', value: [line.date1, line.price1] },
        { name: 'end', value: [line.date2, line.price2] },
      ],
      symbol: 'none',
      // The badge is a markPoint, NOT a label on the end data item.
      //
      // A per-datum label renders relative to that point's symbol, and this series draws none
      // (`symbol: 'none'`), so the label had nothing to attach to and never appeared. markPoint
      // carries its own symbol and label, which is why it is the pattern already used elsewhere in
      // this file for the declination and planetary line tags.
      // Composite tools (Fibonacci, pitchfork) label their own lines and choose their own dash
      // style, so `endLabel` and `lineType` are honoured here rather than each tool needing a new
      // renderer. A tool line never carries a measurement badge — it is construction geometry, not
      // a measured move — so the two label paths are mutually exclusive by construction.
      markPoint: (badge || line.endLabel) ? {
        silent: true,
        symbol: 'circle',
        // A tool line's label marks a level, not a point on it, so it gets no dot.
        symbolSize: badge ? 6 : 0,
        itemStyle: { color: lineColor },
        label: {
          show: true,
          formatter: badge || line.endLabel,
          // Measured moves alternate side to stay legible when several end near one bar; a tool's
          // own label always sits to the right, where the level it names runs out.
          position: badge ? badgeSide : 'right',
          distance: 8,
          color: badge ? '#fff' : lineColor,
          backgroundColor: badge ? lineColor : 'transparent',
          padding: [3, 6],
          borderRadius: 3,
          fontSize: 11,
          fontWeight: 'bold',
        },
        // Anchored to the second point — where the eye ends up after following the line.
        data: [{ coord: [line.date2, line.price2] }],
      } : undefined,
      lineStyle: {
        color: lineColor,
        width: line.lineWidth || 2,
        // Fibonacci levels are dotted so they read as reference levels rather than as drawn lines.
        type: line.lineType || 'solid',
      },
      z: 100, // Ensure trendlines are rendered on top
    };
  });

  // === Moving Average (MA) config ===
  // Calculate closing prices for MA, regardless of chart type
  const closingPrices = barData.ohlc.map(bar => (bar && bar[1] != null) ? bar[1] : null);
  // Calculate up to 3 MAs
  const maSeriesArr = (maConfigs || []).filter(m => m.enabled).slice(0, 3).map((ma, idx) => {
    const period = Math.max(1, Math.floor(ma.period));
    const maData = closingPrices.map((val, i, arr) => {
      if (i < period - 1) return null;
      let sum = 0;
      let hasNull = false;
      for (let j = i - period + 1; j <= i; j++) { if (arr[j] == null) { hasNull = true; break; } sum += arr[j]; }
      if (hasNull) return null;
      return sum / period;
    });
    return {
      type: 'line',
      name: `MA${idx + 1}`,
      data: maData,
      lineStyle: {
        color: ma.color,
        width: 2,
        type: 'dashed',
      },
      showSymbol: false,
      smooth: false,
      z: 90 + idx,
    };
  });
  // === End MA config ===

  let mainSeries;
  // End MA config
  if (chartType === 'candlestick') {
    mainSeries = {
      id: '__candle__',
      type: 'candlestick',
      name: 'Price',
      data: barData.ohlc,
      itemStyle: {
        color: chartColors.candlestickUp,
        color0: chartColors.candlestickDown,
        borderColor: chartColors.candlestickUp,
        borderColor0: chartColors.candlestickDown,
      },
      ...(markLines.length > 0 ? {
        markLine: {
          silent: true,
          symbol: ['none', 'none'],
          data: markLines,
        },
      } : {}),
      ...(markAreas.length > 0 ? {
        markArea: {
          silent: true,
          data: markAreas
        },
      } : {}),
    };
    // Add candlestick pattern markPoints if enabled
    if (showPatterns && barData && barData.ohlc && barData.ohlc.length > 0) {
      let patternMarkPoints = [];
      for (let i = 0; i < barData.ohlc.length; ++i) {
        const patterns = detectAllPatterns(barData.ohlc, i);
        // Filter patterns by visibility config
        const visiblePatterns = patterns.filter(p => patternVisibility && patternVisibility[p] !== false);
        if (visiblePatterns.length > 0) {
          patternMarkPoints.push({
            name: visiblePatterns.join(', '),
            coord: [barData.dates[i], barData.ohlc[i][1]], // dot above high
            value: '',
            symbol: 'circle',
            symbolSize: 2.4,
            itemStyle: { color: getPatternColor(visiblePatterns) },
            label: {
              show: true,
              position: 'top',
              formatter: function(params) {
                return params.name;
              },
              color: '#333',
              fontSize: 10,
              backgroundColor: 'rgba(255,255,255,0.8)',
              borderRadius: 3,
              padding: [2, 4],
              overflow: 'truncate',
              fontFamily: 'monospace',
              fontWeight: 'bold',
              rich: {}
            },
            tooltip: { show: true, formatter: visiblePatterns.join(', ') }
          });
        }
      }
      if (patternMarkPoints.length > 0) {
        mainSeries.markPoint = {
          data: patternMarkPoints,
          symbol: 'circle',
          symbolSize: 2.4,
          label: {
            show: true,
            position: 'top',
            formatter: function(params) {
              return params.name;
            },
            color: '#333',
            fontSize: 10,
            backgroundColor: 'rgba(255,255,255,0.8)',
            borderRadius: 3,
            padding: [2, 4],
            overflow: 'truncate',
            fontFamily: 'monospace',
            fontWeight: 'bold',
            rich: {}
          },
          tooltip: { show: true }
        };
      }
    }
  } else if (chartType === 'hidden') {
    // The invisible view (admin only — see utils/chartViewMode.js).
    //
    // Invisible, NOT absent. Three things hang off the price series and all of them have to
    // survive:
    //
    //   markLine / markArea  every study overlay — the planetary sign bands, cycle markers,
    //                        highlighted date ranges — is attached to THIS series. Dropping the
    //                        series would take the whole study with it, which is the opposite of
    //                        what hiding the price is for.
    //   data                 the y-axis scales to the series it holds. Emptying the data would
    //                        rescale the axis, and every overlay drawn in price coordinates would
    //                        move with it.
    //   the series itself    removing it renumbers the series array and orphans the '__candle__'
    //                        id that EChartComponent patches by during study reveals.
    //
    // So: same series, same data, same markings — drawn with zero opacity. `silent` stops it
    // answering hover, so there is no tooltip or crosshair snap on a line nobody can see.
    mainSeries = {
      id: '__candle__',
      type: 'line',
      name: 'Price',
      data: closingPrices,
      lineStyle: { opacity: 0, width: 0 },
      itemStyle: { opacity: 0 },
      areaStyle: undefined,
      showSymbol: false,
      silent: true,
      smooth: false,
      ...(markLines.length > 0 ? {
        markLine: {
          silent: true,
          symbol: ['none', 'none'],
          data: markLines,
        },
      } : {}),
      ...(markAreas.length > 0 ? {
        markArea: {
          silent: true,
          data: markAreas
        },
      } : {}),
    };
    // No candlestick pattern markPoints here, deliberately: they label individual price bars, so
    // leaving them on would float annotations over bars that are not drawn.
  } else {
    // For line chart, use closing prices only
    mainSeries = {
      id: '__candle__',
      type: 'line',
      name: 'Price',
      data: closingPrices, // close price
      lineStyle: {
        color: chartColors.candlestickUp,
        width: 2
      },
      ...(markLines.length > 0 ? {
        markLine: {
          silent: true,
          symbol: ['none', 'none'],
          data: markLines,
        },
      } : {}),
      ...(markAreas.length > 0 ? {
        markArea: {
          silent: true,
          data: markAreas
        },
      } : {}),
      showSymbol: false,
      smooth: false
    };
    // Add candlestick pattern markPoints if enabled (for line chart too, above close)
    if (showPatterns && barData && barData.ohlc && barData.ohlc.length > 0) {
      let patternMarkPoints = [];
      for (let i = 0; i < barData.ohlc.length; ++i) {
        const patterns = detectAllPatterns(barData.ohlc, i);
        // Filter patterns by visibility config
        const visiblePatterns = patterns.filter(p => patternVisibility && patternVisibility[p] !== false);
        if (visiblePatterns.length > 0) {
          patternMarkPoints.push({
            name: visiblePatterns.join(', '),
            coord: [barData.dates[i], barData.ohlc[i][3]], // dot at close
            value: '',
            symbol: 'circle',
            symbolSize: 2.4,
            itemStyle: { color: getPatternColor(visiblePatterns) },
            label: {
              show: true,
              position: 'top',
              formatter: function(params) {
                return params.name;
              },
              color: '#333',
              fontSize: 10,
              backgroundColor: 'rgba(255,255,255,0.8)',
              borderRadius: 3,
              padding: [2, 4],
              overflow: 'truncate',
              fontFamily: 'monospace',
              fontWeight: 'bold',
              rich: {}
            },
            tooltip: { show: true, formatter: visiblePatterns.join(', ') }
          });
        }
      }
      if (patternMarkPoints.length > 0) {
        mainSeries.markPoint = {
          data: patternMarkPoints,
          symbol: 'circle',
          symbolSize: 2.4,
          label: {
            show: true,
            position: 'top',
            formatter: function(params) {
              return params.name;
            },
            color: '#333',
            fontSize: 10,
            backgroundColor: 'rgba(255,255,255,0.8)',
            borderRadius: 3,
            padding: [2, 4],
            overflow: 'truncate',
            fontFamily: 'monospace',
            fontWeight: 'bold',
            rich: {}
          },
          tooltip: { show: true }
        };
      }
    }
  }

  const volumeSeries = {
    id: '__volume__',
    type: 'bar',
    name: 'Volume',
    data: barData.volumes.map((volume, index) => ({
      value: volume,
      itemStyle: {
        color: barData.ohlc[index][1] > barData.ohlc[index][0] ? chartColors.volumeUp : chartColors.volumeDown,
      },
    })),
    xAxisIndex: 1,
    yAxisIndex: 2, // Corrected to map to the volume y-axis
  };

  // --- Utility: Scale series to volume panel ---
  function scaleSeriesToVolumePanel(dataArr, barDataVolumes) {
    const maxVolume = Math.max(...barDataVolumes, 1);
    let min = Infinity, max = -Infinity;
    for (const v of dataArr) {
      if (typeof v === 'number') {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
    // Avoid divide by zero
    if (max === min) max = min + 1;
    // Scale each value to 0..maxVolume
    return dataArr.map(v => ((v - min) / (max - min)) * maxVolume);
  }

  // --- Sine Line in Volume Panel ---
  // --- Three Sine Cycles in Volume Panel ---
  const sine1Raw = barData.dates.map((date, i) => {
    const angle = (i % 360) * (Math.PI / 180);
    return Math.sin(angle);
  });
  const sine2Raw = barData.dates.map((date, i) => {
    const angle = ((i + 120) % 360) * (Math.PI / 180); // phase shift by 120°
    return Math.sin(angle);
  });
  const sine3Raw = barData.dates.map((date, i) => {
    const angle = ((i + 240) % 360) * (Math.PI / 180); // phase shift by 240°
    return Math.sin(angle);
  });
  const sine1Scaled = scaleSeriesToVolumePanel(sine1Raw, barData.volumes);
  const sine2Scaled = scaleSeriesToVolumePanel(sine2Raw, barData.volumes);
  const sine3Scaled = scaleSeriesToVolumePanel(sine3Raw, barData.volumes);
  const sine1LineData = barData.dates.map((date, i) => [date, sine1Scaled[i]]);
  const sine2LineData = barData.dates.map((date, i) => [date, sine2Scaled[i]]);
  const sine3LineData = barData.dates.map((date, i) => [date, sine3Scaled[i]]);
  const sine1LineSeries = {
    type: 'line',
    name: 'Sine Blue',
    data: sine1LineData,
    symbol: 'none',
    lineStyle: { color: 'blue', width: 2 },
    xAxisIndex: 1,
    yAxisIndex: 1,
    z: 50,
  };
  const sine2LineSeries = {
    type: 'line',
    name: 'Sine Red',
    data: sine2LineData,
    symbol: 'none',
    lineStyle: { color: 'red', width: 2 },
    xAxisIndex: 1,
    yAxisIndex: 1,
    z: 51,
  };
  const sine3LineSeries = {
    type: 'line',
    name: 'Sine Green',
    data: sine3LineData,
    symbol: 'none',
    lineStyle: { color: 'green', width: 2 },
    xAxisIndex: 1,
    yAxisIndex: 1,
    z: 52,
  };

  // Return all series, including MA, as an array for EChartComponent
  return { mainSeries, maSeries: maSeriesArr, trendLineSeries, planetaryLineSeries, volumeSeries, sine1LineSeries, sine2LineSeries, sine3LineSeries, allFormattedDates: barData.dates };

};
